/**
 * @lines98/client/services/meta-api — typed wrapper around the meta endpoints:
 * `GET /meta/state` (read) + `POST /meta/change-display-name` (mutation) +
 * `GET /meta/leaderboard/master-jeweler` (рейтинг по каратам).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (ADR-0015 server-truth): meta-surfaces (HUD / Профиль /
 * «Помощники» / борд) читают мета-состояние игрока как SERVER-TRUTH — балансы
 * (gold / shards / karat) + пул камней + прогрессия + гномы + игровая статистика.
 * Клиент — проекция + оптимистичный UX, НИКОГДА не источник истины (параллель
 * `score_verified`, anti-cheat.md). Балансы приватны (per-player): read требует auth,
 * гость → 401. Борд «Мастер-ювелир» публичен (optional-auth) — гости видят рейтинг.
 *
 * **«Животные» удалены (workshop-animals-removal.md, owner-directive п.14 2026-06-21):**
 * jewelry-обёртки (`craftJewelry / upgradeJewelry / setActiveJewelry /
 * presentMasterpiece / forgeGem / insertGem` + тип `JewelryItem` + поля `jewelry[]` /
 * `activeJewelryId` / `hasMasterpieceToPresent`) СНЯТЫ вместе с мастерской. Остались:
 * `getMetaState` (балансы/пул/прогрессия/гномы/статистика), `changeDisplayName` (ник),
 * `getMasterJewelerLeaderboard` (борд по каратам — карат паркуется, борд жив).
 *
 * Boundary (I-1 p2w wall): это reward-data слой. Он НИКОГДА не входит обратно в
 * движок / replayScore / честные лидерборды. Борд по каратам — by-design витрина
 * трат, отдельная от честных скилл-бордов.
 *
 * Cross-references:
 *   - packages/server/src/routes/{meta-state,meta-mutations,master-jeweler-
 *     leaderboard}.ts (contract source) + meta/state.ts + leaderboard/master-jeweler.ts.
 *   - ADR-0015 D-3 (server-truth); ADR-0019 (смена ника).
 *   - services/daily-api.ts + leaderboard-api.ts (read/write discriminated-union
 *     pattern образцы). services/api-client.ts (Bearer injection + envelope shape).
 */

import { GEM_POOL_COLORS } from '@lines98/shared/config/meta';
import type { GameMode } from '@lines98/shared/types/game';
import { playerDisplayParts } from '@lines98/shared/util';

import { apiClient } from './api-client.js';
import { mintIdempotencyKey } from '../lib/idempotency-key.js';

// =============================================================================
// Types
// =============================================================================

// JewelryItem (изделие-зверь) УДАЛЁН вместе с «животными» (workshop-animals-removal.md).

/**
 * Одна выданная ачивка игрока (Срез 4a/4b) — проекция `player_achievements` row.
 * Имя/копи/бейдж рендерит клиент по `achievementId` (см. lib/achievement-catalog).
 * Server-truth: факт разблокировки — серверный, клиент не выдаёт.
 */
export interface UnlockedAchievement {
  /** Ключ из `ACHIEVEMENT_CATALOG` (стабильный, НЕ меняется). */
  readonly achievementId: string;
  /** Под-тир ступени (0-based; одна ачивка может иметь несколько ступеней). */
  readonly tierIndex: number;
  /** ISO-время разблокировки. */
  readonly unlockedAt: string;
}

/** Одна выданная веха-награда уровня (слот Книги / пакет / титул). Срез 4a/4b. */
export interface GrantedLevelReward {
  /** Уровень-веха, на которой выдана награда. */
  readonly level: number;
  /** Вид награды (`slot` — слот Книги / `bundle` — пакет / `title` — титул). */
  readonly rewardKind: string;
  /** ISO-время выдачи. */
  readonly grantedAt: string;
}

/**
 * Проекция прогрессии аккаунта (server-truth) — уровень / xp / прогресс-к-следующему
 * / выданные ачивки / веха-награды / счётчик слотов Книги. Срез 4a сервер отдаёт это
 * блоком `progression` в `GET /meta/state`; хаб-Профиль 4b рисует из этих полей.
 * I-1: display-only, НЕ влияет на score/ранг/тай-брейк.
 */
export interface ProgressionState {
  /** Накопленный XP. */
  readonly xp: number;
  /** Текущий уровень. */
  readonly level: number;
  /** XP, набранный ВНУТРИ текущего уровня (числитель прогресс-бара). */
  readonly xpIntoLevel: number;
  /** XP для всего текущего уровня (знаменатель прогресс-бара). */
  readonly xpForLevel: number;
  /** XP до следующего уровня. */
  readonly xpToNext: number;
  /** Выданные ачивки (для секции «Достижения»). */
  readonly achievements: readonly UnlockedAchievement[];
  /** Выданные веха-награды уровня. */
  readonly levelRewards: readonly GrantedLevelReward[];
  /** Слоты Книги, заработанные вехами уровня (= COUNT reward_kind='slot'). */
  readonly earnedSlots: number;
}

/**
 * Публичная игровая статистика профиля (server-truth, display-only) — проекция
 * серверного блока `profile_stats` (DEP-1..4, profile-stats-dashboard). Питает
 * KPI-плитки «статистика-первого» Профиля: партий сыграно / лучший счёт по режимам /
 * дневной стрик / ранг доски дня. I-1: НЕ влияет на score/ранг — отображение.
 * Forward-compat: на stale-server без блока → дефолты (нули / пустой / null).
 */
export interface ProfileStats {
  /** Всего верифицированных зачётных партий (DEP-1). */
  readonly gamesTotal: number;
  /** Всего зачётов доски дня (DEP-1). */
  readonly dailyCount: number;
  /**
   * СКОЛЬКО режимов сыграно (DEP-1) — счётчик ≥0 (не перечень). Контракт
   * frontend-test-creator (profile-stats-projection.test.ts): KPI-плитка показывает
   * число «режимов сыграно». Wire-форма различается: тест шлёт `modes_ever_played` как
   * число; реальный backend (meta-state.ts) — как массив имён режимов. Нормализуем к
   * КОЛИЧЕСТВУ валидных режимов в обоих случаях (forward/back-compat по обеим формам).
   */
  readonly modesEverPlayed: number;
  /**
   * Лучший счёт по режимам (DEP-2). Ключи — ТОЛЬКО сыгранные режимы (отсутствие
   * ключа = не играл; UI показывает пусто, не fake-ноль). Контракт test-creator —
   * плоская запись `bestByMode`. Wire-ключ: `best_by_mode` (контракт теста) ИЛИ
   * `best_score_by_mode` (реальный backend) — читаем оба (back-compat).
   */
  readonly bestByMode: Readonly<Partial<Record<GameMode, number>>>;
  /**
   * Текущий дневной стрик — последовательные дни с зачётом доски дня до сегодня/
   * вчера (DEP-3). 0, если стрик прерван (или нет зачётов).
   */
  readonly currentDailyStreak: number;
  /**
   * Ранг игрока в сегодняшнем лидерборде доски дня (DEP-4). `null`, если игрок не
   * сыграл сегодня зачёт доски дня (в т.ч. гость без записи) — UI покажет
   * «разблокируется входом», НЕ ошибку.
   */
  readonly dailyRank: number | null;
  /**
   * Per-mode статистика для таблицы Профиля (profile-dashboard.md §2.7): запись
   * keyed по ряду (`predict`/`classic`/`blind`/`daily`) → `{gamesCount, bestScore,
   * scoreSum, averageScore}`. Присутствуют ТОЛЬКО сыгранные ряды (нет ключа = не
   * играл → таблица рисует honest-empty «0»/«—»). `averageScore` — server-truth
   * (сервер делит, клиент не пересчитывает). Forward-compat: stale-server → `{}`.
   */
  readonly modeStats: Readonly<Partial<Record<ProfileStatRowKey, ProfileModeStat>>>;
}

/**
 * Ключ строки таблицы режимов Профиля (profile-dashboard.md §2.7): играбельный
 * режим движка + отдельная строка `'daily'` (Доска дня). Зеркало серверного
 * `ProfileStatRowKey` (meta/profile-stats.ts).
 */
export type ProfileStatRowKey = GameMode | 'daily';

/**
 * Статистика одного ряда таблицы режимов (profile-dashboard.md §2.7). Server-truth,
 * display-only (I-1). Присутствует только если в ряду сыграна ≥1 партия.
 */
export interface ProfileModeStat {
  /** Кол-во верифицированных партий ряда (≥1). */
  readonly gamesCount: number;
  /** Лучший счёт ряда. */
  readonly bestScore: number;
  /** Суммарный счёт ряда (провенанс среднего). */
  readonly scoreSum: number;
  /** Средний счёт ряда (`scoreSum / gamesCount`, посчитан сервером). */
  readonly averageScore: number;
}

/**
 * Server-truth projection of the player's meta state. Mirrors the server
 * `MetaStateResponse` success branch (`meta/state.ts` MetaStateProjection): balances
 * (gold/shards/karat/xp/level) + jewelry inventory (Срез 2b) + progression block
 * (Срез 4a — level / achievements / level-rewards). karat/xp/player_level
 * present (defaults) — written by later срезы.
 */
export interface MetaStateSuccess {
  readonly ok: true;
  /** Золото — мягкая валюта прогресса. */
  readonly gold: number;
  /** Осколки — расходник-материал. */
  readonly shards: number;
  /** Вечный карат (Срез 3 начинает писать; до тех пор 0). */
  readonly karat: number;
  /** XP уровня игрока (Срез 4; до тех пор 0). */
  readonly xp: number;
  /** Текущий уровень игрока (Срез 4; дефолт 1). */
  readonly playerLevel: number;
  /**
   * Прогрессия аккаунта (Срез 4a — уровень / xp / ачивки / веха-награды). Хаб-Профиль
   * (Срез 4b) рисует из этого блока. Forward-compat: на stale-server без блока —
   * дефолт (level 1, xp 0, пустые списки) — экран не падает.
   */
  readonly progression: ProgressionState;
  /**
   * Пул камней (`meta-gems-resource.md` §2.1, T2 client): индекс 1..7 = сколько камней
   * этого цвета в запасе игрока; `[0]` sentinel 0; длина 8. Server-truth (I-G6 / AC-G11)
   * для HUD «Помощники» + Профиля. Нули, если игрок ничего не добыл. Forward-compat:
   * на stale-server без блока → нули (экран не падает).
   */
  readonly gemPool: readonly number[];
  /**
   * Публичный ник (server-truth `players.display_name`) — `null`, если ещё не задан
   * (Вариант B). Профиль РЕКОНСИЛИТ им свою identity на загрузке → устраняет рассинхрон
   * «stale-клиент думает имя не задано (free) ↔ сервер знает что задано (paid)»
   * (owner-bug 2026-06-21). Forward-compat: stale-server без поля → `null`.
   */
  readonly displayName: string | null;
  // jewelry[] / activeJewelryId / hasMasterpieceToPresent УДАЛЕНЫ вместе с «животными»
  // (workshop-animals-removal.md): мастерская снята, ответ /meta/state их не несёт.
  /**
   * Игровая статистика профиля (DEP-1..4 — партий / лучший счёт по режимам / дневной
   * стрик / ранг доски дня). «Статистика-первый» Профиль рисует KPI-плитки из этого
   * блока. Server-truth, display-only (I-1). Forward-compat: на stale-server без блока
   * → дефолты (нули / пустой / null) — экран не падает.
   */
  readonly profileStats: ProfileStats;
  /**
   * Блок гномов (GN-2b/GN-4b, server-truth, meta-gnomes.md §4.4 rev 0.3): 7 слотов —
   * открытые + locked. Каждый: `color` + `unlocked` (+ открытый несёт `level`/
   * `prestigeCount`/`unlockedAtMilestone`). Плашка «Гномы» рисует силуэт locked-слота +
   * открытую карточку открытого. Forward-compat: на stale-server без блока → `[]`
   * (UI трактует все 7 как locked, AC-12) — экран не падает.
   */
  readonly gnomes: readonly GnomeStateWire[];
  /**
   * Сколько вех открытия уже выдано (0..7, server-truth, §2.0). РОСТ между ресинками =
   * триггер reveal-очереди (AC-17). Forward-compat: stale-server → 0.
   */
  readonly gnomeUnlocksGranted: number;
  /**
   * Уровень следующей вехи (`= GNOME_UNLOCK_LEVELS[gnomeUnlocksGranted]`) для строки
   * «следующий гном на уровне N». `null`, если все 7 открыты (AC-19). NO-HARDCODE:
   * число вехи — server-truth, клиент его не считает/не хранит (AC-3/AC-16). stale → null.
   */
  readonly nextUnlockLevel: number | null;
}

/**
 * Camel-проекция одного слота гнома из `gnomes[]` (server-truth, §4.1 rev 0.3). Открытый
 * несёт level/prestigeCount/unlockedAtMilestone; locked — только `unlocked:false`. Экран
 * достраивает недостающие до 7 слотов (AC-12) и маппит на `GnomeState` (lib/gnome-state).
 */
export interface GnomeStateWire {
  /** Цвет 1..7 (= индекс движка = арт-индекс, §4.2). */
  readonly color: number;
  /** Открыт ли гном (rev 0.3: locked-слоты тоже в проекции). */
  readonly unlocked: boolean;
  /** Уровень 1..GNOME_MAX_LEVEL (для locked = 0). */
  readonly level: number;
  /** Число престижей (≥0; locked = 0). */
  readonly prestigeCount: number;
  /** Веха, на которой открыт (1..7), или null для locked / legacy. */
  readonly unlockedAtMilestone: number | null;
}

export type MetaStateError =
  /** Network / fetch reject — retry-able. */
  | { readonly ok: false; readonly error: 'network' }
  /** 401 — guest (no identity / expired). HUD hides itself rather than erroring. */
  | { readonly ok: false; readonly error: 'unauthenticated' }
  /** 5xx / unexpected shape — retry-able. */
  | { readonly ok: false; readonly error: 'server_error'; readonly status: number }
  /** 429 — per-player read rate-limit. */
  | { readonly ok: false; readonly error: 'rate_limited'; readonly status: number };

export type MetaStateResult = MetaStateSuccess | MetaStateError;

// =============================================================================
// Internal wire shape (server JSON — snake_case)
// =============================================================================

// JewelryItemWire УДАЛЁН вместе с «животными» (workshop-animals-removal.md).

/** Server JSON for one unlocked achievement (snake_case — progression-state.ts). */
interface UnlockedAchievementWire {
  achievement_id: string;
  tier_index: number;
  unlocked_at: string;
}

/** Server JSON for one granted level reward (snake_case — progression-state.ts). */
interface GrantedLevelRewardWire {
  level: number;
  reward_kind: string;
  granted_at: string;
}

/** Server JSON for the progression block (snake_case — progression-state.ts). */
interface ProgressionWire {
  xp: number;
  level: number;
  xp_into_level: number;
  xp_for_level: number;
  xp_to_next: number;
  achievements: UnlockedAchievementWire[];
  level_rewards: GrantedLevelRewardWire[];
  earned_slots: number;
}

/**
 * Server JSON for the profile-stats block (snake_case). Two wire shapes are tolerated:
 *  - `modes_ever_played`: реальный backend (meta-state.ts) шлёт МАССИВ имён режимов;
 *    контракт теста — ЧИСЛО. Читаем оба (массив → длина, число → как есть).
 *  - лучший счёт по режимам приходит ключом `best_by_mode` (контракт теста) ИЛИ
 *    `best_score_by_mode` (реальный backend). Читаем любой присутствующий.
 */
interface ProfileStatsWire {
  games_total: number;
  daily_count: number;
  modes_ever_played: string[] | number;
  best_by_mode?: Record<string, number>;
  best_score_by_mode?: Record<string, number>;
  current_daily_streak: number;
  daily_rank: number | null;
  /** Per-mode таблица (profile-dashboard.md §2.7). Absent на stale-server → `{}`. */
  mode_stats?: Record<string, ProfileModeStatWire>;
}

/** Wire shape одной строки таблицы режимов (snake_case; mirrors server). */
interface ProfileModeStatWire {
  games_count: number;
  best_score: number;
  score_sum: number;
  average_score: number;
}

interface MetaStateWireSuccess {
  ok: true;
  gold: number;
  shards: number;
  karat: number;
  xp: number;
  player_level: number;
  // active_jewelry_id / has_masterpiece_to_present / jewelry[] удалены вместе с
  // «животными» (workshop-animals-removal.md) — сервер их больше не шлёт.
  /** Пул камней (`meta-gems-resource.md` §2.1): индекс 1..7 = счётчик цвета; длина 8. */
  gem_pool?: number[];
  /** Публичный ник (server-truth). Absent/null = не задан (Вариант B; forward-compat). */
  display_name?: string | null;
  progression?: ProgressionWire;
  /** Игровая статистика профиля (DEP-1..4). Absent на stale-server → дефолты. */
  profile_stats?: ProfileStatsWire;
  /** Блок гномов (GN-2b/GN-4b, §4.4). Absent на stale-server → все locked (forward-compat). */
  gnomes?: GnomeWire[];
  /** Сколько вех открытия выдано (0..7). Absent → 0. */
  gnome_unlocks_granted?: number;
  /** Уровень следующей вехи (server-truth) или null если все 7 открыты. Absent → null. */
  next_unlock_level?: number | null;
}

/** Server JSON for one gnome slot (snake_case — meta/gnomes-state.ts `GnomeProjection`). */
interface GnomeWire {
  color: number;
  unlocked: boolean;
  level: number;
  prestige_count: number;
  unlocked_at_milestone: number | null;
}

/**
 * Нормализовать пул камней в массив длины `GEM_POOL_COLORS + 1` (индекс 1..7 = счётчик,
 * `[0]` sentinel 0). Forward-compat: stale-server без блока / битая форма → все нули
 * (карточка/HUD рисуют «нет камней», не падают). Каждый элемент — конечное число ≥ 0.
 */
function toGemPool(raw: number[] | undefined): number[] {
  const pool = new Array<number>(GEM_POOL_COLORS + 1).fill(0);
  if (!Array.isArray(raw)) return pool;
  for (let color = 1; color <= GEM_POOL_COLORS; color++) {
    const v = raw[color];
    pool[color] = Number.isFinite(v) && v! > 0 ? Math.floor(v!) : 0;
  }
  return pool;
}

/**
 * Default progression projection for a stale server that omits the block (Срез 4a
 * always sends it). Keeps the screen from crashing — level 1, no XP, empty lists.
 */
const DEFAULT_PROGRESSION: ProgressionState = {
  xp: 0,
  level: 1,
  xpIntoLevel: 0,
  xpForLevel: 0,
  xpToNext: 0,
  achievements: [],
  levelRewards: [],
  earnedSlots: 0,
};

/** Map the snake_case progression wire block to the camelCase `ProgressionState`. */
function toProgressionState(w: ProgressionWire | undefined): ProgressionState {
  if (
    typeof w !== 'object' ||
    w === null ||
    !Number.isFinite(w.level) ||
    !Array.isArray(w.achievements) ||
    !Array.isArray(w.level_rewards)
  ) {
    return DEFAULT_PROGRESSION;
  }
  return {
    xp: Number.isFinite(w.xp) ? w.xp : 0,
    level: w.level,
    xpIntoLevel: Number.isFinite(w.xp_into_level) ? w.xp_into_level : 0,
    xpForLevel: Number.isFinite(w.xp_for_level) ? w.xp_for_level : 0,
    xpToNext: Number.isFinite(w.xp_to_next) ? w.xp_to_next : 0,
    achievements: w.achievements.map((a) => ({
      achievementId: a.achievement_id,
      tierIndex: Number.isFinite(a.tier_index) ? a.tier_index : 0,
      unlockedAt: a.unlocked_at,
    })),
    levelRewards: w.level_rewards.map((r) => ({
      level: r.level,
      rewardKind: r.reward_kind,
      grantedAt: r.granted_at,
    })),
    earnedSlots: Number.isFinite(w.earned_slots) ? w.earned_slots : 0,
  };
}

/**
 * Default profile-stats projection for a stale server that omits the block
 * (profile-stats-dashboard always sends it). Keeps the screen from crashing —
 * zero counts, empty mode maps, null rank (UI shows empty/«разблокируется входом»).
 */
const DEFAULT_PROFILE_STATS: ProfileStats = {
  gamesTotal: 0,
  dailyCount: 0,
  modesEverPlayed: 0,
  bestByMode: {},
  currentDailyStreak: 0,
  dailyRank: null,
  modeStats: {},
};

/** Allowed game modes (shape-guard against drift in the snake_case wire). */
const VALID_GAME_MODES: ReadonlySet<GameMode> = new Set<GameMode>(['predict', 'classic', 'blind']);

/** Allowed таблица-row-keys (играбельные режимы + Доска дня). */
const VALID_ROW_KEYS: ReadonlySet<ProfileStatRowKey> = new Set<ProfileStatRowKey>([
  'predict',
  'classic',
  'blind',
  'daily',
]);

/** Narrow an unknown string to a `GameMode` (drops anything unrecognised). */
function isGameMode(s: string): s is GameMode {
  return VALID_GAME_MODES.has(s as GameMode);
}

/** Narrow an unknown string to a `ProfileStatRowKey` (drops anything unrecognised). */
function isRowKey(s: string): s is ProfileStatRowKey {
  return VALID_ROW_KEYS.has(s as ProfileStatRowKey);
}

/**
 * Map the snake_case profile-stats wire block to the camelCase `ProfileStats`
 * (DEP-1..4). Forward-compat / shape-guard: a missing or malformed block → safe
 * defaults (no crash); unrecognised mode keys are dropped (no fake KPI). Each
 * count coerced to a finite non-negative integer.
 */
function toProfileStats(w: ProfileStatsWire | undefined): ProfileStats {
  if (typeof w !== 'object' || w === null) return DEFAULT_PROFILE_STATS;
  const toCount = (v: unknown): number =>
    Number.isFinite(v) && (v as number) > 0 ? Math.floor(v as number) : 0;

  // modesEverPlayed → КОЛИЧЕСТВО сыгранных режимов. Массив (реальный backend) → длина
  // (валидных режимов); число (контракт теста) → как есть; иначе 0 (без NaN-цифры).
  const modesEverPlayed = Array.isArray(w.modes_ever_played)
    ? w.modes_ever_played.filter((m): m is GameMode => typeof m === 'string' && isGameMode(m)).length
    : toCount(w.modes_ever_played);

  // bestByMode — читаем `best_by_mode` (тест) ИЛИ `best_score_by_mode` (backend);
  // оставляем только распознанные режимы с конечным счётом (без fake-нуля).
  const best: Partial<Record<GameMode, number>> = {};
  const rawBest = w.best_by_mode ?? w.best_score_by_mode;
  if (typeof rawBest === 'object' && rawBest !== null) {
    for (const [mode, score] of Object.entries(rawBest)) {
      if (isGameMode(mode) && Number.isFinite(score)) best[mode] = Math.floor(score);
    }
  }

  // modeStats — per-row {gamesCount, bestScore, scoreSum, averageScore}. Только
  // распознанные row-keys (predict/classic/blind/daily) с конечными числами; иначе
  // ключ дропается (без fake-цифры). averageScore — server-truth (клиент не делит).
  const modeStats: Partial<Record<ProfileStatRowKey, ProfileModeStat>> = {};
  if (typeof w.mode_stats === 'object' && w.mode_stats !== null) {
    for (const [key, s] of Object.entries(w.mode_stats)) {
      if (!isRowKey(key) || typeof s !== 'object' || s === null) continue;
      const gamesCount = toCount(s.games_count);
      if (gamesCount <= 0) continue; // ряд без партий не показываем (honest-empty)
      modeStats[key] = {
        gamesCount,
        bestScore: Number.isFinite(s.best_score) ? Math.floor(s.best_score) : 0,
        scoreSum: Number.isFinite(s.score_sum) ? Math.floor(s.score_sum) : 0,
        averageScore: Number.isFinite(s.average_score) ? Math.floor(s.average_score) : 0,
      };
    }
  }

  return {
    gamesTotal: toCount(w.games_total),
    dailyCount: toCount(w.daily_count),
    modesEverPlayed,
    bestByMode: best,
    currentDailyStreak: toCount(w.current_daily_streak),
    // daily_rank is `null` for «no daily row today» (incl. guest) — preserve null;
    // a finite rank stays as a positive integer; anything else degrades to null.
    dailyRank:
      Number.isFinite(w.daily_rank) && (w.daily_rank as number) > 0
        ? Math.floor(w.daily_rank as number)
        : null,
    modeStats,
  };
}

/**
 * Map the snake_case gnomes wire block to camelCase `GnomeStateWire[]` (GN-2b, §4.4).
 * Forward-compat / shape-guard: a missing or malformed block → `[]` (the screen treats
 * all 7 as locked, AC-12). Each slot's numeric fields are coerced to finite values; a
 * row without a valid `color` (1..7) is dropped (no fake slot). The screen builds the
 * full 7-slot layout from these opened rows (`lib/gnome-state.buildGnomes`).
 */
function toGnomes(raw: GnomeWire[] | undefined): GnomeStateWire[] {
  if (!Array.isArray(raw)) return [];
  const out: GnomeStateWire[] = [];
  for (const g of raw) {
    if (typeof g !== 'object' || g === null) continue;
    const color = Number(g.color);
    if (!Number.isInteger(color) || color < 1 || color > 7) continue;
    out.push({
      color,
      unlocked: g.unlocked === true,
      level: Number.isFinite(g.level) ? Math.max(0, Math.floor(g.level)) : 0,
      prestigeCount: Number.isFinite(g.prestige_count) ? Math.max(0, Math.floor(g.prestige_count)) : 0,
      unlockedAtMilestone:
        Number.isFinite(g.unlocked_at_milestone) && (g.unlocked_at_milestone as number) > 0
          ? Math.floor(g.unlocked_at_milestone as number)
          : null,
    });
  }
  return out;
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Fetch the current player's meta balances from the server (server-truth).
 *
 * Idempotent (GET, no state mutation). The currency HUD calls this on mount and
 * after a reward accrual (to project the new balances). A guest (401) resolves
 * to `{ ok: false, error: 'unauthenticated' }` — the HUD treats it as «no
 * balances to show» rather than an error toast.
 *
 * @returns `MetaStateResult` discriminated union. Callers branch on `.ok`.
 */
export async function getMetaState(): Promise<MetaStateResult> {
  let response;
  try {
    response = await apiClient.get<MetaStateWireSuccess>('/meta/state');
  } catch {
    // ApiClientError (network reject).
    return { ok: false, error: 'network' };
  }

  if (!response.ok) {
    if (response.status === 401) return { ok: false, error: 'unauthenticated' };
    if (response.status === 429) {
      return { ok: false, error: 'rate_limited', status: response.status };
    }
    return { ok: false, error: 'server_error', status: response.status };
  }

  const body = response.body;
  // Defensive shape check — a breaking server change fails loudly via
  // `server_error`, never a silent NaN in the HUD.
  if (
    typeof body !== 'object' ||
    body === null ||
    !Number.isFinite((body as MetaStateWireSuccess).gold) ||
    !Number.isFinite((body as MetaStateWireSuccess).shards) ||
    !Number.isFinite((body as MetaStateWireSuccess).karat) ||
    !Number.isFinite((body as MetaStateWireSuccess).xp) ||
    !Number.isFinite((body as MetaStateWireSuccess).player_level)
  ) {
    return { ok: false, error: 'server_error', status: response.status };
  }

  return {
    ok: true,
    gold: body.gold,
    shards: body.shards,
    karat: body.karat,
    xp: body.xp,
    playerLevel: body.player_level,
    // Пул камней (T2, §2.1): нормализуем в массив длины 8 (forward-compat — нули, если
    // сервер не прислал блок). Server-truth для HUD + Профиля (AC-G11).
    gemPool: toGemPool(body.gem_pool),
    // Публичный ник (server-truth) — Профиль реконсилит им identity (устраняет stale-
    // рассинхрон free/paid). Forward-compat: stale-server без поля → null (= не задан).
    displayName: body.display_name ?? null,
    // Срез 4a progression block — display-only account progression. Defaults if a
    // stale server omits it (forward-compat; the screen never crashes on a missing block).
    progression: toProgressionState(body.progression),
    // profile-stats-dashboard (DEP-1..4) — игровая статистика для KPI-плиток Профиля.
    // Display-only (I-1). Defaults на stale-server (forward-compat — экран не падает).
    profileStats: toProfileStats(body.profile_stats),
    // Гномы (GN-2b/GN-4b, §4.4) — 7 слотов (открытые + locked) + счётчик вех + следующая
    // веха. Server-truth (I-Gn4). NO-HARDCODE: число вехи (next_unlock_level) приходит с
    // сервера, клиент не хранит массив [2,6,11,...] (AC-3/AC-16). Forward-compat: stale-
    // server без блока → [] / 0 / null (UI трактует все 7 как locked, AC-12).
    gnomes: toGnomes(body.gnomes),
    gnomeUnlocksGranted:
      Number.isFinite(body.gnome_unlocks_granted) && (body.gnome_unlocks_granted as number) > 0
        ? Math.floor(body.gnome_unlocks_granted as number)
        : 0,
    nextUnlockLevel:
      typeof body.next_unlock_level === 'number' && Number.isFinite(body.next_unlock_level)
        ? body.next_unlock_level
        : null,
  };
}

// toJewelryItem + craft/upgrade verdicts/wire-types (CraftResult / UpgradeResult /
// CraftWireSuccess / UpgradeWireSuccess) УДАЛЕНЫ вместе с «животными»
// (workshop-animals-removal.md). Остался общий `MutationWireFailure` + хелперы ниже
// (их переиспользует change-display-name).

// =============================================================================
// Mutation transport helpers (переиспользуются change-display-name)
// =============================================================================

/** Wire failure shape мутаций: `{ ok: false, reason }`. */
interface MutationWireFailure {
  ok: false;
  reason: string;
}

// craftJewelry / upgradeJewelry УДАЛЕНЫ вместе с «животными» (workshop-animals-removal.md).

/** Map a non-2xx HTTP status to a mutation transport reason. */
function mutationTransportReason(status: number): 'unauthenticated' | 'rate_limited' | 'server_error' {
  if (status === 401) return 'unauthenticated';
  if (status === 429) return 'rate_limited';
  return 'server_error';
}

/** Narrow an unknown response body to the `{ ok: false, reason }` failure shape. */
function isWireFailure(body: unknown): body is MutationWireFailure {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as MutationWireFailure).ok === false &&
    typeof (body as MutationWireFailure).reason === 'string'
  );
}

// isCraftSuccess / isUpgradeSuccess УДАЛЕНЫ вместе с «животными» (workshop-animals-removal.md).

// =============================================================================
// Смена ника — POST /meta/change-display-name (ADR-0019)
// (jewelry-мутации active-jewelry / present-masterpiece / forge-gem / insert-gem
//  УДАЛЕНЫ вместе с «животными», workshop-animals-removal.md.)
// =============================================================================

// SetActiveJewelryResult / PresentMasterpieceResult / ForgeGemResult / InsertGemResult
// УДАЛЕНЫ вместе с «животными» (workshop-animals-removal.md).

/**
 * Verdict of `POST /meta/change-display-name` (server-truth, profile-dashboard.md
 * §2.6 / ADR-0019). Успех → ник сменён; `goldSpent` = 0 для ПЕРВОЙ установки
 * (бесплатно, `wasFirstSet:true`) или `NICKNAME_CHANGE_COST` для платной смены.
 * `insufficient_gold` → золота < стоимости (ник НЕ изменён, золото НЕ списано);
 * `invalid_display_name` → ник не прошёл политику (длина/profanity). Server-truth
 * ресинк баланса/имени после успеха: `getMetaState` + identity-events (фронт).
 */
export type ChangeDisplayNameResult =
  | {
      readonly ok: true;
      /** Новый ник (trimmed, server-truth). */
      readonly displayName: string;
      /** Списано золота (0 для первой установки, иначе NICKNAME_CHANGE_COST). */
      readonly goldSpent: number;
      /** `true` если это была первая установка (с пустого → бесплатно). */
      readonly wasFirstSet: boolean;
    }
  | {
      readonly ok: false;
      readonly reason:
        | 'invalid_display_name'
        | 'insufficient_gold'
        | 'unauthenticated'
        | 'network'
        | 'server_error'
        | 'rate_limited';
    };

/** Wire success — `POST /meta/change-display-name` (camelCase — route returns verbatim). */
interface ChangeDisplayNameWireSuccess {
  ok: true;
  displayName: string;
  goldSpent: number;
  wasFirstSet: boolean;
}

// ActiveJewelry / PresentMasterpiece / ForgeGem / InsertGem wire-types + functions
// (setActiveJewelry / presentMasterpiece) УДАЛЕНЫ вместе с «животными»
// (workshop-animals-removal.md).

// forgeGem (сплав осколков → камень мастерской) УДАЛЁН вместе с «животными»
// (workshop-animals-removal.md).

/**
 * Сменить публичный ник (`POST /meta/change-display-name`, profile-dashboard.md §2.6,
 * ADR-0019). ПЕРВАЯ установка (с пустого) — бесплатно; последующие смены —
 * `NICKNAME_CHANGE_COST` золота (server-truth: клиент не списывает локально, I-Prof-3).
 * Стоимость/решение «первая ли установка» — сервер (клиент шлёт только `display_name`).
 *
 * После успеха фронт ОБЯЗАН: (1) ресинкнуть баланс через `getMetaState` (золото −
 * стоимость), (2) эмитнуть `identity-events` (`emitIdentityChanged`), чтобы шапка
 * Профиля И account-chip рейла перерисовались из одного источника (§2.6 п.5 / I-Prof-6).
 *
 * **Идемпотентность (как forgeGem).** У смены ника НЕТ БД-натур-ключа → шлём стабильный
 * per-действие `X-Idempotency-Key` (ADR-0005 Option B). Один вызов = один nonce;
 * внутренний ретрай транспорта переиспользует header → золото списывается / ник
 * меняется РОВНО раз. 409 (`unknown`) — параллельный дубль того же ключа → маппится в
 * `server_error` (UI ресинкает), без двойного эффекта.
 *
 * @param displayName — желаемый ник (сырой; сервер trim'ит + валидирует).
 */
export async function changeDisplayName(displayName: string): Promise<ChangeDisplayNameResult> {
  // Stable per-action nonce — survives the api-client internal retry, so a single
  // «Сменить» gesture spends gold + changes the name at most once (ADR-0005).
  const idempotencyKey = mintIdempotencyKey();
  let response;
  try {
    response = await apiClient.post<ChangeDisplayNameWireSuccess | MutationWireFailure>(
      '/meta/change-display-name',
      { display_name: displayName },
      { headers: { 'X-Idempotency-Key': idempotencyKey } },
    );
  } catch {
    return { ok: false, reason: 'network' };
  }
  if (!response.ok) return { ok: false, reason: mutationTransportReason(response.status) };

  const body = response.body;
  if (isWireFailure(body)) {
    const r = body.reason;
    if (r === 'invalid_display_name' || r === 'insufficient_gold') {
      return { ok: false, reason: r };
    }
    return { ok: false, reason: 'server_error' };
  }
  const b = body as ChangeDisplayNameWireSuccess;
  if (
    typeof b !== 'object' ||
    b === null ||
    b.ok !== true ||
    typeof b.displayName !== 'string' ||
    !Number.isFinite(b.goldSpent)
  ) {
    return { ok: false, reason: 'server_error' };
  }
  return {
    ok: true,
    displayName: b.displayName,
    goldSpent: b.goldSpent,
    wasFirstSet: b.wasFirstSet === true,
  };
}

// insertGem (вставка камня в радугу зверя) УДАЛЁН вместе с «животными»
// (workshop-animals-removal.md).

// =============================================================================
// Гномы — POST /meta/upgrade-gnome + /meta/prestige-gnome (GN-4, meta-gnomes.md §4.4)
// =============================================================================

/**
 * Вердикт `POST /meta/upgrade-gnome` (server-truth, meta-gnomes.md §4.4 / AC-Gn4).
 * Зеркало серверного `UpgradeGnomeResult` (`meta/mutations/upgrade-gnome.ts`) + транспорт.
 * Успех → уровень гнома поднят на 1, валюта списана сервером (`goldSpent`/`shardsSpent`).
 * Доменные отказы (server-truth): `gnome_locked` (гном не открыт), `max_level` (уже L20 —
 * там престиж), `stale_level` (CAS-промах: double-click/рассинхрон → no-op, валюта НЕ
 * списана), `not_enough_resources` (не хватило золота/осколков). Транспортные:
 * `unauthenticated` (гость 401) / `network` / `server_error` / `rate_limited`.
 *
 * **Анти-чит (I-Gn4 / AC-9):** клиент шлёт ТОЛЬКО intent (`{color, expected_level}`) —
 * стоимость/уровень-как-факт он НЕ присылает. Цена и валидация — server-truth.
 */
export type UpgradeGnomeResult =
  | {
      readonly ok: true;
      /** Цвет апгрейженного гнома (1..7). */
      readonly color: number;
      /** Новый уровень (`expectedLevel + 1`, server-truth). */
      readonly newLevel: number;
      /** Списано золота (server-truth). */
      readonly goldSpent: number;
      /** Списано осколков (server-truth; 0 до GNOME_SHARD_COST_FROM_LEVEL). */
      readonly shardsSpent: number;
    }
  | {
      readonly ok: false;
      readonly reason:
        | 'gnome_locked'
        | 'max_level'
        | 'stale_level'
        | 'not_enough_resources'
        | 'unauthenticated'
        | 'network'
        | 'server_error'
        | 'rate_limited';
    };

/**
 * Вердикт `POST /meta/prestige-gnome` (server-truth, meta-gnomes.md §2.4/§4.4 / AC-Gn5).
 * Зеркало серверного `PrestigeGnomeResult` (`meta/mutations/prestige-gnome.ts`) + транспорт.
 * Успех → гном сброшен на L1, `prestige_count += 1` (необратимо; UI подтверждает confirm).
 * Доменные отказы: `gnome_locked` (не открыт), `not_max_level` (не на L20 / повтор-гонка
 * после уже-применённого престижа → no-op). Транспортные: как у апгрейда.
 *
 * **Анти-чит (I-Gn4 / AC-9):** body = РОВНО `{color}` (без `expected_level` — престиж
 * гейтится server-side natural-CAS по `level == MAX`). Никакого баланса/уровня-как-факта.
 */
export type PrestigeGnomeResult =
  | {
      readonly ok: true;
      /** Цвет престижированного гнома (1..7). */
      readonly color: number;
      /** Новый уровень после reset (всегда 1, server-truth). */
      readonly newLevel: number;
      /** Новый счётчик престижей (`prestige_count + 1`, server-truth). */
      readonly newPrestigeCount: number;
    }
  | {
      readonly ok: false;
      readonly reason:
        | 'gnome_locked'
        | 'not_max_level'
        | 'unauthenticated'
        | 'network'
        | 'server_error'
        | 'rate_limited';
    };

/** Wire success — `POST /meta/upgrade-gnome` (camelCase — route returns result verbatim). */
interface UpgradeGnomeWireSuccess {
  ok: true;
  color: number;
  newLevel: number;
  goldSpent: number;
  shardsSpent: number;
}

/** Wire success — `POST /meta/prestige-gnome` (camelCase — route returns result verbatim). */
interface PrestigeGnomeWireSuccess {
  ok: true;
  color: number;
  newLevel: number;
  newPrestigeCount: number;
}

/**
 * Апгрейд гнома цвета `color` на 1 уровень (`POST /meta/upgrade-gnome`, meta-gnomes.md
 * §4.4, AC-Gn4). По образцу `changeDisplayName` (discriminated-union, defensive shape-check,
 * `mutationTransportReason`).
 *
 * **Body = РОВНО `{ color, expected_level }` — intent, НЕ баланс (AC-9 анти-чит).** Стоимость
 * и валидация — server-truth (клиент их НЕ шлёт). `expectedLevel` — уровень, который клиент
 * видит сейчас (CAS-гард): сервер поднимет level ТОЛЬКО если он совпал — double-click/ретрай
 * видит уже-поднятый уровень → `stale_level` no-op (валюта не списана дважды, AC-Gn10).
 *
 * **БЕЗ X-Idempotency-Key (REUSE-AC):** в отличие от смены ника (нет БД-натур-ключа), у
 * gnome-апгрейда защита от двойного эффекта — серверный CAS по `expected_level`. Idempotency-
 * header ему НЕ нужен (не изобретаем nonce там, где он не требуется).
 *
 * После любого ответа экран ресинкает server-truth через `getMetaStateShared()` (reconcile).
 *
 * @param color — цвет гнома (1..7).
 * @param expectedLevel — текущий уровень из server-truth-модели (CAS-гард, не из DOM).
 */
export async function upgradeGnome(
  color: number,
  expectedLevel: number,
): Promise<UpgradeGnomeResult> {
  let response;
  try {
    response = await apiClient.post<UpgradeGnomeWireSuccess | MutationWireFailure>(
      '/meta/upgrade-gnome',
      // Intent only — color + expected_level (CAS). NO gold/shards/cost/level-as-truth (AC-9).
      { color, expected_level: expectedLevel },
    );
  } catch {
    return { ok: false, reason: 'network' };
  }
  if (!response.ok) return { ok: false, reason: mutationTransportReason(response.status) };

  const body = response.body;
  if (isWireFailure(body)) {
    const r = body.reason;
    if (
      r === 'gnome_locked' ||
      r === 'max_level' ||
      r === 'stale_level' ||
      r === 'not_enough_resources'
    ) {
      return { ok: false, reason: r };
    }
    return { ok: false, reason: 'server_error' };
  }
  const b = body as UpgradeGnomeWireSuccess;
  // Defensive shape-check (зеркало changeDisplayName): сломанный «успех» падает явно
  // (server_error), а не отдаёт NaN/undefined в карточку.
  if (
    typeof b !== 'object' ||
    b === null ||
    b.ok !== true ||
    !Number.isFinite(b.color) ||
    !Number.isFinite(b.newLevel) ||
    !Number.isFinite(b.goldSpent) ||
    !Number.isFinite(b.shardsSpent)
  ) {
    return { ok: false, reason: 'server_error' };
  }
  return {
    ok: true,
    color: b.color,
    newLevel: b.newLevel,
    goldSpent: b.goldSpent,
    shardsSpent: b.shardsSpent,
  };
}

/**
 * Престиж гнома цвета `color` (`POST /meta/prestige-gnome`, meta-gnomes.md §2.4/§4.4,
 * AC-Gn5). По образцу `changeDisplayName`. Необратимо (UI подтверждает `confirm-dialog`
 * ДО вызова).
 *
 * **Body = РОВНО `{ color }` — БЕЗ `expected_level`, БЕЗ баланса (AC-9 анти-чит).** Престиж
 * гейтится server-side natural-CAS по `level == GNOME_MAX_LEVEL`: повтор/гонка двух
 * устройств после уже-применённого престижа видит `level=1 ≠ MAX` → `not_max_level` no-op
 * (счётчик не растёт дважды). Поэтому idempotency-header/expected_level НЕ нужны (REUSE-AC).
 *
 * После любого ответа экран ресинкает server-truth через `getMetaStateShared()` (reconcile).
 *
 * @param color — цвет гнома (1..7).
 */
export async function prestigeGnome(color: number): Promise<PrestigeGnomeResult> {
  let response;
  try {
    response = await apiClient.post<PrestigeGnomeWireSuccess | MutationWireFailure>(
      '/meta/prestige-gnome',
      // Intent only — color. NO expected_level/gold/shards/level-as-truth (AC-9).
      { color },
    );
  } catch {
    return { ok: false, reason: 'network' };
  }
  if (!response.ok) return { ok: false, reason: mutationTransportReason(response.status) };

  const body = response.body;
  if (isWireFailure(body)) {
    const r = body.reason;
    if (r === 'gnome_locked' || r === 'not_max_level') {
      return { ok: false, reason: r };
    }
    return { ok: false, reason: 'server_error' };
  }
  const b = body as PrestigeGnomeWireSuccess;
  if (
    typeof b !== 'object' ||
    b === null ||
    b.ok !== true ||
    !Number.isFinite(b.color) ||
    !Number.isFinite(b.newLevel) ||
    !Number.isFinite(b.newPrestigeCount)
  ) {
    return { ok: false, reason: 'server_error' };
  }
  return {
    ok: true,
    color: b.color,
    newLevel: b.newLevel,
    newPrestigeCount: b.newPrestigeCount,
  };
}

// =============================================================================
// Борд «Мастер-ювелир» — GET /meta/leaderboard/master-jeweler (рейтинг по каратам)
// =============================================================================

/**
 * Одна строка борда «Мастер-ювелир» (рейтинг по каратам). Имя + `#`-тег выводятся
 * server-formed РАЗДЕЛЬНО (player_name = ник/«Игрок», player_tag = суффикс), тем же
 * единым источником, что daily/all-time (no drift; owner-decision 2026-06-09 — тег
 * мельче+тихим). Fallback — SHARED `playerDisplayParts`.
 */
export interface MasterJewelerRow {
  readonly rank: number;
  /** Owner UUID (для derive label + own-detection). */
  readonly playerId: string;
  /** Server-formed имя — ник или «Игрок» (полный размер). */
  readonly playerName: string;
  /** Server-formed `#`-тег — non-PII суффикс (мельче + тихим, §5.3). */
  readonly playerTag: string;
  /**
   * Уровень прогрессии игрока — server-truth (та же кривая, что `/meta/state`).
   * Display-only бейдж рядом с именем КАЖДОГО игрока (owner-decision 2026-06-11),
   * НЕ ранкинг-ключ (ранг по карату, AC-9). Floor 1 если поле опущено (старый сервер).
   */
  readonly level: number;
  /** Вечный карат (счёт борда). */
  readonly karat: number;
  /** `true` если это строка зрителя. */
  readonly isOwn: boolean;
}

export type MasterJewelerLeaderboardResult =
  | { readonly ok: true; readonly rows: ReadonlyArray<MasterJewelerRow> }
  | { readonly ok: false; readonly error: 'network' }
  | { readonly ok: false; readonly error: 'server_error'; readonly status: number }
  | { readonly ok: false; readonly error: 'rate_limited'; readonly status: number };

interface MasterJewelerRowWire {
  rank: number;
  player_id: string;
  /** Server-formed имя — ник или «Игрок» (ADR rev3 §3 / §5.1). */
  player_name?: string;
  /** Server-formed `#`-тег — non-PII суффикс (§5.3 / Q-6). */
  player_tag?: string;
  /** Уровень прогрессии (display-only бейдж; absent на старом сервере). */
  level?: number;
  karat: number;
  is_own: boolean;
}

interface MasterJewelerWireResponse {
  rows: MasterJewelerRowWire[];
  limit: number;
  offset: number;
}

/**
 * Прочитать страницу борда «Мастер-ювелир» (рейтинг по каратам). Публичен
 * (optional-auth — гости видят борд; своя строка подсвечивается только
 * аутентифицированным). I-1: by-design витрина трат, не честный скилл-борд.
 *
 * @param args.limit — размер страницы (опц., сервер дефолтит/зажимает).
 * @param args.offset — смещение (опц., ≥ 0).
 */
export async function getMasterJewelerLeaderboard(
  args: { limit?: number; offset?: number } = {},
): Promise<MasterJewelerLeaderboardResult> {
  const params = new URLSearchParams();
  if (args.limit !== undefined) params.set('limit', String(Math.max(1, args.limit)));
  if (args.offset !== undefined) params.set('offset', String(Math.max(0, args.offset)));
  const qs = params.toString();
  const path = qs
    ? `/meta/leaderboard/master-jeweler?${qs}`
    : '/meta/leaderboard/master-jeweler';

  let response;
  try {
    response = await apiClient.get<MasterJewelerWireResponse>(path);
  } catch {
    return { ok: false, error: 'network' };
  }
  if (!response.ok) {
    if (response.status === 429) return { ok: false, error: 'rate_limited', status: response.status };
    return { ok: false, error: 'server_error', status: response.status };
  }
  const body = response.body;
  if (typeof body !== 'object' || body === null || !Array.isArray((body as MasterJewelerWireResponse).rows)) {
    return { ok: false, error: 'server_error', status: response.status };
  }
  const rows: MasterJewelerRow[] = (body as MasterJewelerWireResponse).rows.map((r) => ({
    rank: r.rank,
    playerId: r.player_id,
    // Server-formed имя + `#`-тег РАЗДЕЛЬНО (single source, §5.1/§5.3/Q-6); fallback
    // к shared parts если поля опущены (shape-guard / старый сервер).
    playerName: r.player_name ?? playerDisplayParts(undefined, r.player_id).name,
    playerTag: r.player_tag ?? playerDisplayParts(undefined, r.player_id).tag,
    // Display-only level (server-truth). Floor to curve base (level 1) if omitted.
    level: typeof r.level === 'number' && Number.isFinite(r.level) ? Math.max(1, Math.trunc(r.level)) : 1,
    karat: r.karat,
    isOwn: r.is_own === true,
  }));
  return { ok: true, rows };
}
